import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Edit2, Eye, EyeOff } from 'lucide-react';
import { createApiClient } from '../config/api';

const AdminPanel = ({ onClose }) => {
  const [apiClient] = useState(createApiClient());
  const [view, setView] = useState('trips'); // trips | destinations | auth
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingTripId, setEditingTripId] = useState(null);
  const [editingDestId, setEditingDestId] = useState(null);

  // Trip form state
  const [tripForm, setTripForm] = useState({
    id: '',
    title: '',
    date: '',
    description: '',
    aboutText: '',
  });

  // Destination form state
  const [destForm, setDestForm] = useState({
    id: '',
    title: '',
    description: '',
    coordinates: { lat: 0, lng: 0 },
    s3FolderName: '',
    importantSpots: [],
  });

  const loadTrips = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.listTrips(true); // includeHidden=true for admin
      setTrips(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [apiClient]);

  const loadDestinations = useCallback(async (tripId) => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.listDestinations(tripId, true); // includeHidden=true for admin
      setDestinations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [apiClient]);

  // Load trips on mount
  useEffect(() => {
    if (apiKey && view === 'trips') {
      loadTrips();
    }
  }, [apiKey, view, loadTrips]);

  // Load destinations when trip is selected
  useEffect(() => {
    if (selectedTrip && view === 'destinations') {
      loadDestinations(selectedTrip.id);
    }
  }, [selectedTrip, view, loadDestinations]);

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!tripForm.title || !tripForm.date) {
      setError('Title and date are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (editingTripId) {
        // Update existing trip
        await apiClient.updateTrip(editingTripId, {
          title: tripForm.title,
          date: tripForm.date,
          description: tripForm.description,
          aboutText: tripForm.aboutText,
        });
        setEditingTripId(null);
      } else {
        // Create new trip
        await apiClient.createTrip({
          id: tripForm.id || tripForm.title.toLowerCase().replace(/\s+/g, '-'),
          ...tripForm,
        });
      }
      setTripForm({ id: '', title: '', date: '', description: '', aboutText: '' });
      loadTrips();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleCreateDestination = async (e) => {
    e.preventDefault();
    if (!destForm.title) {
      setError('Destination title is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (editingDestId) {
        // Update existing destination
        await apiClient.updateDestination(selectedTrip.id, editingDestId, {
          title: destForm.title,
          description: destForm.description,
          coordinates: destForm.coordinates,
        });
        setEditingDestId(null);
      } else {
        // Create new destination
        await apiClient.createDestination(selectedTrip.id, {
          id: destForm.id || destForm.title.toLowerCase().replace(/\s+/g, '-'),
          ...destForm,
        });
      }
      setDestForm({
        id: '',
        title: '',
        description: '',
        coordinates: { lat: 0, lng: 0 },
        s3FolderName: '',
        importantSpots: [],
      });
      loadDestinations(selectedTrip.id);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleEditTrip = (trip) => {
    setEditingTripId(trip.id);
    setTripForm({
      id: trip.id,
      title: trip.title,
      date: trip.date,
      description: trip.description || '',
      aboutText: trip.aboutText || '',
    });
  };

  const handleEditDestination = (dest) => {
    setEditingDestId(dest.id);
    setDestForm({
      id: dest.id,
      title: dest.title,
      description: dest.description || '',
      coordinates: dest.coordinates,
      s3FolderName: dest.s3FolderName || '',
      importantSpots: dest.importantSpots || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingTripId(null);
    setEditingDestId(null);
    setTripForm({ id: '', title: '', date: '', description: '', aboutText: '' });
    setDestForm({
      id: '',
      title: '',
      description: '',
      coordinates: { lat: 0, lng: 0 },
      s3FolderName: '',
      importantSpots: [],
    });
  };

  const handleToggleHidden = async (type, tripId, itemId = null) => {
    setLoading(true);
    setError('');
    try {
      if (type === 'trip') {
        const trip = trips.find(t => t.id === tripId);
        await apiClient.updateTrip(tripId, { hidden: !trip.hidden });
      } else {
        const dest = destinations.find(d => d.id === itemId);
        await apiClient.updateDestination(tripId, itemId, { hidden: !dest.hidden });
      }
      if (type === 'trip') {
        loadTrips();
      } else {
        loadDestinations(tripId);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (!apiKey) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg w-full max-w-md">
          <h2 className="text-2xl font-bold mb-4">Admin Access</h2>
          <p className="text-gray-600 mb-4">Enter API key to manage content:</p>
          <input
            type="password"
            placeholder="API Key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mb-4"
          />
          <div className="flex gap-2">
            <button
              onClick={() => setApiKey('')}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={() => apiKey && loadTrips()}
              disabled={!apiKey}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Enter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold">Travel Site Admin</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 px-6 pt-4 border-b border-gray-200">
          <button
            onClick={() => {
              setView('trips');
              handleCancelEdit();
            }}
            className={`px-4 py-2 font-medium transition ${
              view === 'trips'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Trips
          </button>
          {selectedTrip && (
            <button
              onClick={() => {
                setView('destinations');
                handleCancelEdit();
              }}
              className={`px-4 py-2 font-medium transition ${
                view === 'destinations'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Destinations ({selectedTrip.title})
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {view === 'trips' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  {editingTripId ? 'Edit Trip' : 'Create New Trip'}
                </h3>
                <form onSubmit={handleCreateTrip} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Trip title"
                      value={tripForm.title}
                      onChange={(e) =>
                        setTripForm({ ...tripForm, title: e.target.value })
                      }
                      className="px-4 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Date range (e.g., 2024-2025)"
                      value={tripForm.date}
                      onChange={(e) =>
                        setTripForm({ ...tripForm, date: e.target.value })
                      }
                      className="px-4 py-2 border rounded-lg"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Short description"
                    value={tripForm.description}
                    onChange={(e) =>
                      setTripForm({ ...tripForm, description: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  <textarea
                    placeholder="About text (longer description)"
                    value={tripForm.aboutText}
                    onChange={(e) =>
                      setTripForm({ ...tripForm, aboutText: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg h-24"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {editingTripId ? 'Update Trip' : 'Create Trip'}
                    </button>
                    {editingTripId && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-6 py-2 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Trips ({trips.length})</h3>
                <div className="space-y-2">
                  {loading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : trips.length === 0 ? (
                    <p className="text-gray-500">No trips yet</p>
                  ) : (
                    trips.map((trip) => (
                      <div
                        key={trip.id}
                        className={`p-4 border rounded-lg transition ${
                          trip.hidden
                            ? 'bg-gray-100 border-gray-300'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <button
                            onClick={() => {
                              setSelectedTrip(trip);
                              setView('destinations');
                            }}
                            className="flex-1 text-left"
                          >
                            <div className="font-semibold flex items-center gap-2">
                              {trip.title}
                              {trip.hidden && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                                  Hidden
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">{trip.date}</div>
                          </button>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleToggleHidden('trip', trip.id)}
                              className="p-2 text-gray-600 hover:bg-gray-200 rounded"
                              title={trip.hidden ? 'Show' : 'Hide'}
                            >
                              {trip.hidden ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEditTrip(trip)}
                              className="p-2 text-gray-600 hover:bg-gray-200 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {view === 'destinations' && selectedTrip && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  {editingDestId ? 'Edit Destination' : 'Create New Destination'} in{' '}
                  {selectedTrip.title}
                </h3>
                <form onSubmit={handleCreateDestination} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Destination title"
                    value={destForm.title}
                    onChange={(e) =>
                      setDestForm({ ...destForm, title: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  {destForm.title && !editingDestId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <p className="text-blue-800">
                        📁 Photos will be stored in: <code className="font-mono bg-blue-100 px-2 py-1 rounded">{selectedTrip.date.split('-')[0]}_{selectedTrip.date.split('-')[1]}_{selectedTrip.title.replace(/\s+/g, '_').toLowerCase()}/{destForm.title.replace(/\s+/g, '_').toLowerCase()}</code>
                      </p>
                    </div>
                  )}
                  <textarea
                    placeholder="Description"
                    value={destForm.description}
                    onChange={(e) =>
                      setDestForm({ ...destForm, description: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg h-20"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="number"
                      placeholder="Latitude"
                      step="0.0001"
                      value={destForm.coordinates.lat}
                      onChange={(e) =>
                        setDestForm({
                          ...destForm,
                          coordinates: {
                            ...destForm.coordinates,
                            lat: parseFloat(e.target.value),
                          },
                        })
                      }
                      className="px-4 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      placeholder="Longitude"
                      step="0.0001"
                      value={destForm.coordinates.lng}
                      onChange={(e) =>
                        setDestForm({
                          ...destForm,
                          coordinates: {
                            ...destForm.coordinates,
                            lng: parseFloat(e.target.value),
                          },
                        })
                      }
                      className="px-4 py-2 border rounded-lg"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {editingDestId ? 'Update Destination' : 'Create Destination'}
                    </button>
                    {editingDestId && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-6 py-2 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Destinations ({destinations.length})
                </h3>
                <div className="space-y-2">
                  {loading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : destinations.length === 0 ? (
                    <p className="text-gray-500">No destinations yet</p>
                  ) : (
                    destinations.map((dest) => (
                      <div
                        key={dest.id}
                        className={`p-4 border rounded-lg transition ${
                          dest.hidden
                            ? 'bg-gray-100 border-gray-300'
                            : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-semibold flex items-center gap-2">
                              {dest.title}
                              {dest.hidden && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                                  Hidden
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              Coords: {dest.coordinates.lat.toFixed(4)}, {dest.coordinates.lng.toFixed(4)}
                            </div>
                            <div className="text-sm text-gray-600">
                              S3: {dest.s3FolderName}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleToggleHidden('destination', selectedTrip.id, dest.id)}
                              className="p-2 text-gray-600 hover:bg-gray-200 rounded"
                              title={dest.hidden ? 'Show' : 'Hide'}
                            >
                              {dest.hidden ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEditDestination(dest)}
                              className="p-2 text-gray-600 hover:bg-gray-200 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
